import { useCallback, useEffect, useRef, useState } from 'react';
import { transcribeWithWhisper } from '../services/voice';
import { hasOpenAIApiKey } from '../services/openai';

export interface UseSpeechRecognitionOptions {
  lang?: string;
  onResult: (text: string) => void;
  onError?: (message: string) => void;
}

export interface UseSpeechRecognitionResult {
  isSupported: boolean;
  isWhisper: boolean;
  isListening: boolean;
  interimTranscript: string;
  error: string | null;
  start: () => void;
  stop: () => void;
}

interface BrowserSpeechRecognition extends EventTarget {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  onstart: (() => void) | null;
  onend: (() => void) | null;
  onerror: ((event: { error: string }) => void) | null;
  onresult: ((event: { resultIndex: number; results: SpeechRecognitionResultList }) => void) | null;
  start: () => void;
  stop: () => void;
  abort: () => void;
}

interface SpeechRecognitionResultItem {
  isFinal: boolean;
  0: { transcript: string };
}

interface SpeechRecognitionResultList {
  readonly length: number;
  [index: number]: SpeechRecognitionResultItem;
}

type BrowserSpeechRecognitionConstructor = new () => BrowserSpeechRecognition;

function getRecognitionConstructor(): BrowserSpeechRecognitionConstructor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    SpeechRecognition?: BrowserSpeechRecognitionConstructor;
    webkitSpeechRecognition?: BrowserSpeechRecognitionConstructor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

/**
 * Speech-to-text hook.
 * Uses the browser's native Speech Recognition API when available and falls
 * back to OpenAI Whisper (via the microphone) when it is not.
 */
export function useSpeechRecognition({
  lang = 'ur-PK',
  onResult,
  onError,
}: UseSpeechRecognitionOptions): UseSpeechRecognitionResult {
  const [isListening, setIsListening] = useState(false);
  const [interimTranscript, setInterimTranscript] = useState('');
  const [error, setError] = useState<string | null>(null);

  const recognitionRef = useRef<BrowserSpeechRecognition | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const onResultRef = useRef(onResult);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onResultRef.current = onResult;
  }, [onResult]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  const RecognitionConstructor = getRecognitionConstructor();
  const isSupported = RecognitionConstructor !== null;
  const isWhisper = !isSupported && hasOpenAIApiKey();

  const stop = useCallback(() => {
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch {
        // no-op
      }
      recognitionRef.current = null;
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }
    setIsListening(false);
    setInterimTranscript('');
  }, []);

  const startWhisper = useCallback(() => {
    setError(null);
    chunksRef.current = [];
    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        mediaStreamRef.current = stream;
        const recorder = new MediaRecorder(stream);
        mediaRecorderRef.current = recorder;
        recorder.ondataavailable = (event) => {
          if (event.data && event.data.size > 0) {
            chunksRef.current.push(event.data);
          }
        };
        recorder.onstop = () => {
          const blob = new Blob(chunksRef.current, { type: recorder.mimeType || 'audio/webm' });
          setIsListening(false);
          transcribeWithWhisper(blob, 'ur')
            .then((text) => {
              if (text) onResultRef.current(text);
            })
            .catch((err) => {
              setError(err instanceof Error ? err.message : 'Transcription failed');
              onErrorRef.current?.(err instanceof Error ? err.message : 'Transcription failed');
            });
        };
        recorder.onerror = () => {
          setIsListening(false);
        };
        recorder.start();
        setIsListening(true);
      })
      .catch(() => {
        setError('Microphone permission denied');
        onErrorRef.current?.('Microphone permission denied');
      });
  }, []);

  const start = useCallback(() => {
    if (isListening) return;

    if (!isSupported) {
      if (isWhisper) {
        startWhisper();
        return;
      }
      setError('Speech recognition is not supported in this browser');
      onErrorRef.current?.('Speech recognition is not supported in this browser');
      return;
    }

    const RecognitionAPI = getRecognitionConstructor();
    if (!RecognitionAPI) return;

    setError(null);
    setInterimTranscript('');

    let recognition = recognitionRef.current;
    if (!recognition) {
      recognition = new RecognitionAPI();
      recognition.lang = lang;
      recognition.continuous = false;
      recognition.interimResults = true;
      recognition.maxAlternatives = 1;

      recognition.onstart = () => setIsListening(true);
      recognition.onend = () => {
        recognitionRef.current = null;
        setIsListening(false);
      };

      recognition.onerror = (event) => {
        recognitionRef.current = null;
        setIsListening(false);
        const message = event.error === 'not-allowed' || event.error === 'service-not-allowed'
          ? 'Microphone permission denied'
          : 'Speech recognition error';
        setError(message);
        onErrorRef.current?.(message);
      };

      recognition.onresult = (event) => {
        let finalText = '';
        let interim = '';
        for (let i = event.resultIndex; i < event.results.length; i += 1) {
          const result = event.results[i];
          const transcript = result[0]?.transcript ?? '';
          if (result.isFinal) {
            finalText += transcript;
          } else {
            interim += transcript;
          }
        }
        setInterimTranscript(interim);
        if (finalText.trim()) {
          onResultRef.current(finalText.trim());
        }
      };

      recognitionRef.current = recognition;
    }

    try {
      recognition.start();
    } catch {
      setIsListening(false);
    }
  }, [isListening, isSupported, isWhisper, lang, startWhisper]);

  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.abort();
        } catch {
          // no-op
        }
      }
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  return {
    isSupported,
    isWhisper,
    isListening,
    interimTranscript,
    error,
    start,
    stop,
  };
}
